/**
 * One half of box 9b: a count for the reporting period and a running total.
 *
 * Injuries and deaths print as the same two-column line, so they share this
 * rather than each carrying its own copy. The single row keeps the edit lock
 * the rest of the 209's tables use: the pencil opens it, the check writes it,
 * and the cross puts the stored values back.
 */
import { useState } from 'react';
import { Check, Pencil, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ICS204_TABLE } from '../../AssignmentList/assignmentCardStyles';

export type ResponderCountTableProps = {
  /** Names the group for screen readers, e.g. 'Responder Injuries'. */
  label: string;
  testId: string;
  thisReportingPeriodLabel: string;
  totalLabel: string;
  thisReportingPeriod: number;
  total: number;
  readOnly?: boolean;
  onSave: (values: { thisReportingPeriod: number; total: number }) => void;
};

/** A blank box means none reported, which the form prints as zero. */
const toCount = (value: string): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && value.trim() !== '' ? parsed : 0;
};

export const ResponderCountTable: React.FC<ResponderCountTableProps> = ({
  label,
  testId,
  thisReportingPeriodLabel,
  totalLabel,
  thisReportingPeriod,
  total,
  readOnly = false,
  onSave,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftPeriod, setDraftPeriod] = useState(String(thisReportingPeriod));
  const [draftTotal, setDraftTotal] = useState(String(total));

  const startEditing = () => {
    setDraftPeriod(String(thisReportingPeriod));
    setDraftTotal(String(total));
    setIsEditing(true);
  };

  const commit = () => {
    onSave({
      thisReportingPeriod: toCount(draftPeriod),
      total: toCount(draftTotal),
    });
    setIsEditing(false);
  };

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
      <span className="tw:text-sm tw:text-muted-foreground">{label}</span>
      <Table className={ICS204_TABLE} data-testid={testId}>
        <TableHeader>
          <TableRow>
            <TableHead>{thisReportingPeriodLabel}</TableHead>
            <TableHead>{totalLabel}</TableHead>
            <TableHead className="tw:w-24" />
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell>
              {isEditing ? (
                <Input
                  type="number"
                  aria-label={thisReportingPeriodLabel}
                  value={draftPeriod}
                  onChange={(event) => setDraftPeriod(event.target.value)}
                />
              ) : (
                <span>{thisReportingPeriod}</span>
              )}
            </TableCell>
            <TableCell>
              {isEditing ? (
                <Input
                  type="number"
                  aria-label={totalLabel}
                  value={draftTotal}
                  onChange={(event) => setDraftTotal(event.target.value)}
                />
              ) : (
                <span>{total}</span>
              )}
            </TableCell>
            <TableCell>
              <div className="tw:flex tw:justify-end tw:gap-1">
                {isEditing ? (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Save ${label}`}
                      onClick={commit}
                    >
                      <Check className="tw:size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Cancel ${label}`}
                      onClick={() => setIsEditing(false)}
                    >
                      <X className="tw:size-4" />
                    </Button>
                  </>
                ) : readOnly ? null : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Edit ${label}`}
                    onClick={startEditing}
                  >
                    <Pencil className="tw:size-4" />
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};

export default ResponderCountTable;
